/**
 * openNuiteeSsoInNewTab
 *
 * Solves the "nothing happens when clicking Book" problem caused by popup blockers
 * silently blocking window.open() called after an async/await chain.
 *
 * Root cause: browsers only allow window.open() during a synchronous user-gesture
 * (click/tap). Once the call stack unwinds past the first await, the gesture is
 * consumed and many browsers (Safari, mobile Chrome, iOS Chrome) block the open
 * without any error or feedback.
 *
 * Solution — "tab first, navigate second":
 *   1. Open about:blank SYNCHRONOUSLY on the click stack (inert — no Next.js bootstrap).
 *   2. Detect if the open was blocked (returns null) and surface a clear modal.
 *   3. Run the async SSO API chain (hotel-id lookup + getSSOLink).
 *   4. On success: assign popup.location.href = loginUrl (navigation, not a new open).
 *   5. On API failure: close the placeholder tab + surface error toast.
 *
 * NOTE on noopener: We intentionally omit "noopener" from the synchronous
 * window.open() call because we need the opener reference to later assign
 * popup.location.href. The placeholder tab navigates immediately to the
 * third-party Nuitee SSO URL, so the opener reference is only held briefly.
 * The third-party page cannot access window.opener once it navigates cross-origin.
 */

import { isNativePlatform } from '@/lib/platform';
import { Browser } from '@capacitor/browser';

export type SsoOpenSurface =
  | 'header'
  | 'place_card'
  | 'booking_modal'
  | 'results'
  | 'profile_dashboard'
  | 'itineraries_page';

export type SsoOpenResult =
  | { status: 'opened'; loginUrl: string }
  | { status: 'blocked'; loginUrl?: string }
  | { status: 'api_error'; errorCode?: string; errorStatus?: number };

export interface OpenNuiteeSsoInNewTabOptions {
  /** Async function that resolves to the Nuitee SSO loginUrl. */
  fetchLoginUrl: () => Promise<string>;
  /** Where the open was triggered from (for analytics). */
  surface?: SsoOpenSurface;
  /** Called when the tab opens successfully and navigation is assigned. */
  onSuccess?: (loginUrl: string) => void;
  /** Called when the popup was blocked (popup === null). loginUrl may be provided
   *  if the API call was still made so a fallback "Open link" can be shown. */
  onBlocked?: (loginUrl?: string) => void;
  /** Called when the SSO/hotel-id API call fails. */
  onApiError?: (errorCode?: string, errorStatus?: number) => void;
}

/**
 * Opens the Nuitee whitelabel in a new tab using the tab-first pattern.
 * Must be called synchronously from a user-gesture handler (onClick, onKeyDown).
 * Returns the result so callers can react (e.g. update loading state).
 */
export async function openNuiteeSsoInNewTab(
  options: OpenNuiteeSsoInNewTabOptions
): Promise<SsoOpenResult> {
  const { fetchLoginUrl, onSuccess, onBlocked, onApiError } = options;

  // Native (Capacitor): no concept of tabs / popup blocking. Fetch the SSO URL
  // and open it in an in-app SFSafariViewController / Custom Tab.
  if (typeof window !== 'undefined' && isNativePlatform()) {
    try {
      const loginUrl = await fetchLoginUrl();
      await Browser.open({ url: loginUrl });
      onSuccess?.(loginUrl);
      return { status: 'opened', loginUrl };
    } catch (err: unknown) {
      const res =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number; data?: { code?: string } } }).response
          : undefined;
      const errorStatus = res?.status ?? (err as { status?: number })?.status;
      const errorCode = res?.data?.code ?? (err as { code?: string })?.code;
      onApiError?.(errorCode, errorStatus);
      return { status: 'api_error', errorCode, errorStatus };
    }
  }

  // Open about:blank rather than a same-origin route. A same-origin Next.js page
  // triggers its own network requests (auth checks, recommendation fetches, etc.)
  // which can interfere with the opener's auth state on Safari (ITP + shared
  // localStorage). about:blank is inert — no requests fire.
  //
  // Omit "noopener" so we keep the popup reference needed to assign location.href
  // after the SSO call. The tab navigates to a cross-origin Nuitee URL immediately,
  // after which the opener reference becomes inaccessible to the child anyway.
  const popup =
    typeof window !== 'undefined'
      ? window.open('about:blank', '_blank', '')
      : null;

  // Write a branded loading page into the blank tab immediately so users see
  // feedback rather than a white screen while the SSO call runs.
  if (popup) {
    try {
      popup.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Loading booking experience...</title>
  <style>
    body { margin: 0; min-height: 100vh; display: flex; align-items: center;
           justify-content: center; background: #f9fafb;
           font-family: 'Manrope', system-ui, sans-serif; }
    .card { background: white; border-radius: 16px;
            box-shadow: 0 4px 24px rgba(0,0,0,0.08);
            padding: 48px 40px; max-width: 440px; width: calc(100% - 48px);
            text-align: center; }
    h1 { margin: 0 0 12px; font-size: 20px; font-weight: 700; color: #111827; }
    p  { margin: 0; font-size: 15px; color: #6b7280; line-height: 1.6; }
    .spinner { width: 40px; height: 40px; border: 3px solid #e5e7eb;
               border-top-color: #111827; border-radius: 50%;
               animation: spin 0.8s linear infinite; margin: 0 auto 24px; }
    @keyframes spin { to { transform: rotate(360deg); } }
  </style>
</head>
<body>
  <div class="card">
    <div class="spinner"></div>
    <h1>Almost there&hellip;</h1>
    <p>Please keep this tab open while we load the booking experience.</p>
  </div>
</body>
</html>`);
      popup.document.close();
    } catch {
      // document.write may be blocked in some sandboxed contexts — safe to ignore.
    }
  }

  if (!popup) {
    // Popup was blocked — still attempt the SSO call so we can offer a manual "Open" link.
    let loginUrl: string | undefined;
    try {
      loginUrl = await fetchLoginUrl();
    } catch {
      // Ignore — we're already in the blocked path; show blocked modal without URL.
    }
    onBlocked?.(loginUrl);
    return { status: 'blocked', loginUrl };
  }

  try {
    const loginUrl = await fetchLoginUrl();

    // location.replace keeps the placeholder out of the back-stack.
    popup.location.replace(loginUrl);

    onSuccess?.(loginUrl);
    return { status: 'opened', loginUrl };
  } catch (err: unknown) {
    // Close the placeholder so users don't see a stuck blank tab.
    try {
      popup.close();
    } catch {
      // Cross-origin guard — ignore if already navigated away.
    }

    const res =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number; data?: { code?: string } } }).response
        : undefined;
    const errorStatus = res?.status ?? (err as { status?: number })?.status;
    const errorCode = res?.data?.code ?? (err as { code?: string })?.code;

    onApiError?.(errorCode, errorStatus);
    return { status: 'api_error', errorCode, errorStatus };
  }
}
